import React, { useState } from 'react';
import { LayoutTemplate, ChevronDown, X } from 'lucide-react';
import { Button } from '../ui/button';

const QUICK_TEMPLATES = [
  {
    id: 'sick_call',
    label: 'Sick Call',
    shortLabel: 'Sick Call',
    template: `HPI: [age]yo [gender] presents to sick call with [chief complaint] x [duration].
Onset: [acute/gradual]. Location: []. Quality: [sharp/dull/aching].
Severity: [1-10]. Timing: [constant/intermittent]. Context: [].
Aggravating: []. Alleviating: []. Associated sx: [].
Prior tx: []. Medications tried: [].

ROS: [pertinent positives and negatives]

PMH: []
Meds: []
Allergies: []

Assessment:
1. [diagnosis] - [ICD-10]

Plan:
- [medication/treatment]
- [follow up]
- [RTD status]`
  },
  {
    id: 'msk',
    label: 'MSK / Musculoskeletal',
    shortLabel: 'MSK',
    template: `HPI: [age]yo [gender] [AD/NG/AR] presents with [body part] pain x [duration].
Mechanism: [overuse/trauma/insidious onset].
Location: [R/L/bilateral] [joint/region].
Quality: [sharp/dull/aching/burning]. Severity: [1-10].
Aggravating: [running/lifting/stairs/prolonged sitting].
Alleviating: [rest/ice/NSAIDs/stretching].
Prior imaging: [XR/MRI results if applicable].
Prior treatment: [PT/injections/bracing].
Functional impact: [unable to run/ruck/lift/perform duties].

ROS: Denies numbness, tingling, weakness, swelling, locking, giving way.

PMH: []
Surgical Hx: []
Meds: []

Assessment:
1. [dx] - [ICD-10]

Plan:
- [conservative tx / PT / imaging / referral]
- Profile: [temporary/permanent] [restrictions]
- Follow up: [timeframe]`
  },
  {
    id: 'annual_pha',
    label: 'Annual / PHA',
    shortLabel: 'Annual',
    template: `HPI: [age]yo [gender] [AD/NG/AR] presents for annual PHA / wellness exam.
No acute complaints. Last PHA: [date].
Deployment history: [].

Health Maintenance:
- Immunizations: [up to date / due for ___]
- Labs: [CBC, CMP, Lipids, A1c, UA - results]
- Dental: [Class ___]
- Vision: [date of last exam]
- Hearing: [H1/H2/H3]
- HIV: [date]

PMH: []
PSH: []
Meds: []
Allergies: []

ROS: [14-system review - pertinent findings]

Assessment:
1. Annual wellness exam - Z00.00
2. [any identified conditions]

Plan:
- Labs ordered: []
- Immunizations given: []
- Referrals: []
- Health counseling: [diet/exercise/sleep/tobacco/alcohol]
- Deployable: [Y/N]
- Follow up: 1 year or PRN`
  },
  {
    id: 'mental_health',
    label: 'Mental Health / BH',
    shortLabel: 'BH',
    template: `HPI: [age]yo [gender] [AD/NG/AR] presents with [mood/anxiety/sleep/stress] concerns x [duration].
Onset: [gradual/acute, trigger if any].
Symptoms: [depressed mood/anhedonia/anxiety/panic/insomnia/irritability/poor concentration].
PHQ-9: [score]. GAD-7: [score]. PCL-5: [score if applicable].
Sleep: [hours, quality, nightmares].
Appetite: [increased/decreased/normal].
Energy: [low/normal].
Social: [isolated/withdrawn/engaged].
Functional impact: [work performance/relationships/physical readiness].
Safety: [denies SI/HI/plan/intent/access to weapons].
Substance use: [alcohol/tobacco/other - frequency].
Prior BH tx: [counseling/medication history].

PMH: []
Meds: []
Family Hx: [MH/substance]

Assessment:
1. [diagnosis] - [ICD-10]

Plan:
- [medication: start/adjust/continue]
- [referral: BH / MFLC / chaplain / substance abuse]
- Safety plan: [reviewed/not indicated]
- Follow up: [2-4 weeks]
- [profile if needed]`
  },
  {
    id: 'profile_eval',
    label: 'Profile Evaluation',
    shortLabel: 'Profile',
    template: `HPI: [age]yo [gender] [AD/NG/AR] presents for profile [initiation/review/extension].
Condition: [diagnosis] x [duration].
Current profile: [temporary/permanent, expiration date if temp].
Current restrictions: [].
Treatment to date: [medications/PT/surgery/injections].
Functional status: [what can/cannot do].
PULHES: [current rating].
MOS requirements: [relevant physical demands].

Current symptoms: [pain level, limitations, progress].
Compliance with treatment: [attending PT, taking meds, following restrictions].

Assessment:
1. [diagnosis] - [ICD-10]
2. [functional limitation]

Plan:
- Profile: [Temp/Perm] [duration]
- Restrictions: [no running/rucking/lifting >___lbs/push-ups/sit-ups]
- Alternate events: [walk/bike/swim]
- MEB consideration: [yes/no/not at this time]
- Follow up: [30/60/90 days]`
  },
  {
    id: 'follow_up',
    label: 'Follow-Up Visit',
    shortLabel: 'Follow-Up',
    template: `HPI: [age]yo [gender] returns for follow-up of [condition] diagnosed [date].
Interval history: [improved/stable/worsened] since last visit.
Current symptoms: [].
Treatment adherence: [taking medications as prescribed / attending PT / following restrictions].
Side effects: [none / ___].
New concerns: [none / ___].

Labs/Imaging since last visit: [results if applicable].

PMH: [unchanged]
Meds: [current list]

Assessment:
1. [diagnosis] - [ICD-10] - [improving/stable/worsening]

Plan:
- [continue/adjust/discontinue current tx]
- [new orders if any]
- Follow up: [timeframe]`
  },
  {
    id: 'med_refill',
    label: 'Medication Refill',
    shortLabel: 'Med Refill',
    template: `HPI: [age]yo [gender] presents for medication refill.
Current medication: [name, dose, frequency] x [duration on med].
Indication: [diagnosis].
Efficacy: [good control / partial / poor].
Side effects: [none reported / ___].
Adherence: [taking as prescribed / missed doses].
Last labs: [date, relevant results].

Assessment:
1. [diagnosis] - [ICD-10] - stable on current regimen

Plan:
- Continue [medication, dose, frequency] #[quantity] refills [number]
- Labs due: [if applicable]
- Follow up: [3-6 months or PRN]`
  },
  {
    id: 'uspstf_screening',
    label: 'USPSTF Screening',
    shortLabel: 'USPSTF',
    template: `CANCER SCREENING / USPSTF RECOMMENDATIONS:

LUNG CANCER:
[age]yo [gender] due for low dose lung CT per USPSTF recommendations.
Criteria: Age 50-80, ≥20 pack-year smoking history, currently smokes or quit within past 15 years.
Recommendation: Annual low-dose computed tomography (LDCT). Grade B.

COLORECTAL CANCER:
[age]yo [gender] due for colorectal cancer screening per USPSTF recommendations.
Criteria: Age 45-75, average risk.
Options: Colonoscopy q10yr, FIT annually, FIT-DNA (Cologuard) q1-3yr, CT colonography q5yr, flexible sigmoidoscopy q5yr.
Recommendation: Screen per patient preference. Grade A (50-75), Grade B (45-49).

BREAST CANCER:
[age]yo female due for breast cancer screening per USPSTF recommendations.
Criteria: Age 40-74, average risk.
Recommendation: Biennial screening mammography. Grade B.

CERVICAL CANCER:
[age]yo female due for cervical cancer screening per USPSTF recommendations.
Criteria: Age 21-65.
Options: Pap smear q3yr (age 21-29), Pap q3yr OR hrHPV q5yr OR co-testing q5yr (age 30-65).
Recommendation: Screen per age-appropriate interval. Grade A.

PROSTATE CANCER:
[age]yo male — discuss prostate cancer screening per USPSTF recommendations.
Criteria: Age 55-69.
Recommendation: Shared decision-making regarding PSA-based screening. Grade C.
Note: Do not screen men ≥70 (Grade D).

AAA (Abdominal Aortic Aneurysm):
[age]yo male with smoking history — one-time screening per USPSTF recommendations.
Criteria: Men age 65-75 who have ever smoked.
Recommendation: One-time screening ultrasonography. Grade B.`
  },
  {
    id: 'deployment',
    label: 'Deployment Health',
    shortLabel: 'Deploy',
    template: `HPI: [age]yo [gender] [AD/NG/AR] [MOS] presents for [pre/post]-deployment health assessment.
Deployment: [location] from [dates].
Exposures: [blast/burn pit/chemicals/noise/infectious disease].
Injuries during deployment: [none / ___].
Current symptoms: [none / ___].

Screening:
- PHQ-9: [score]
- PCL-5: [score]
- TBI screen: [positive/negative]
- Hearing: [baseline/threshold shift]
- Vision: [unchanged / ___]

PMH: [pre-deployment conditions]
Meds: [current]

Assessment:
1. [Pre/Post]-deployment health assessment
2. [any new conditions identified]

Plan:
- [referrals if positive screens]
- [follow up requirements per AR 40-501]
- Deployable: [Y/N]`
  },
];

const QuickTemplates = ({ onApplyTemplate, notes }) => {
  const [open, setOpen] = useState(false);
  const hasNotes = notes && notes.trim().length > 0;

  const handleSelect = (template) => {
    if (hasNotes) {
      // Append after existing notes with separator
      onApplyTemplate(notes + '\n\n' + template.template);
    } else {
      onApplyTemplate(template.template);
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(!open)}
        className="h-6 px-2 text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 gap-1"
        data-testid="quick-templates-btn"
        title="Insert a note template"
      >
        <LayoutTemplate className="w-3 h-3" />
        Templates
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </Button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          
          {/* Dropdown */}
          <div 
            className="absolute left-0 top-full mt-1 z-50 w-64 max-h-[320px] overflow-y-auto rounded-lg border border-border bg-card shadow-xl"
            data-testid="quick-templates-dropdown"
          >
            <div className="p-2 border-b border-border/50 flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground">Quick Templates</span>
              <button onClick={() => setOpen(false)} className="p-0.5 hover:bg-secondary rounded">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
            {hasNotes && (
              <div className="px-2 py-1.5 bg-amber-500/5 border-b border-border/50">
                <p className="text-[10px] text-amber-400">Template will be appended to existing notes</p>
              </div>
            )}
            <div className="p-1">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleSelect(t)}
                  className="w-full text-left px-3 py-2 rounded-md text-sm text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                  data-testid={`template-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export { QuickTemplates, QUICK_TEMPLATES };
export default QuickTemplates;
